/**
 * The berth asset list's model (M8 plan MC-b6b): one row per berth the pilot holds (`career.storage`), each with its
 * state from `berthAccess` (open, frozen, relocating) and what it holds, plus the totals over every berth. Pure and
 * read-only: `ui/berth/AssetList.tsx` renders it from the dashboard's Fleet › Berths, and it has no actions. Storing and
 * taking stay on the docked station's Berth tab (MC-b6a), so a row says so when the player is docked there and is
 * read-only everywhere else.
 *
 * - **State:** the cached view of a berth's station decides (`viewOf`). The cached views cover the current system
 *   only, so a berth elsewhere reads from its stamp alone (open, or relocating once `relocateAfter` is stamped), as
 *   MC-b5's dock pass does (D-500).
 * - **Order:** the berth where the player is docked first, then by station name, then by id.
 */
import { z } from 'zod'
import { berthAccess, isBerthStation, relocationTarget, type BerthAccess, type BerthData } from '../../career/berth'
import { EPSILON, GOOD_NAMES } from '../../career/ops'
import { GOOD_IDS, goodT, goodsT } from '../../career/parts'
import type { BerthEntry, CargoComponent, Career } from '../../career/types'
import { WARE_IDS, type M2Data, type StationId } from '../../data/m2Schemas'
import strategicJson from '../../data/strategic/strategic.json'
import type { StationView } from '../../strategic/types'
import assetListJson from '../berth/assetList.json'
import { formatTonnes } from '../format'
import { BERTH_UI, type BerthText } from './berth'
import { wareName } from './market'

const textSchema = z.strictObject({
  title: z.string().min(1),
  tabHint: z.string().min(1),
  sub: z.string(),
  here: z.string().min(1),
  readOnly: z.string().min(1),
  empty: z.string().min(1),
  noCareer: z.string().min(1),
  totals: z.string().min(1),
  rowTotals: z.string().min(1),
  berthCount: z.strictObject({ one: z.string().min(1), many: z.string().min(1) }),
  componentCount: z.strictObject({ one: z.string().min(1), many: z.string().min(1) }),
  close: z.string().min(1),
})

const assetListSchema = z.strictObject({ comment: z.string(), text: textSchema })
export type AssetListConfig = z.infer<typeof assetListSchema>
export type AssetListText = AssetListConfig['text']

/** "1 berth", "2 berths", "0 components" (PG-2 R2-8): `one` for exactly 1, `many` otherwise. */
function count(forms: Readonly<{ one: string; many: string }>, n: number): string {
  return fill(n === 1 ? forms.one : forms.many, { n })
}

/** The list's config (`ui/berth/assetList.json`), parsed once. */
export const ASSET_LIST_UI: Readonly<AssetListConfig> = Object.freeze(assetListSchema.parse(assetListJson))

/** Game seconds per day for "on day N" (the strategic faction day, as `relocateAfter` is stamped). */
const DAY_S: number = strategicJson.clock.dayS

const MK_NAMES: readonly string[] = Object.freeze(['I', 'II', 'III'])

function fill(template: string, vals: Readonly<Record<string, string | number>>): string {
  return template.replace(/\{(\w+)\}/g, (m, k: string) => (k in vals ? String(vals[k]) : m))
}

export interface BerthAssetsInput {
  data: M2Data
  career: Career | null
  /** The career station the player is docked at, or null in flight. */
  dockedAt: StationId | null
  /** The cached view of berth station `stationId`, or null (no strategic link, another system, not listed yet). */
  viewOf(stationId: StationId): StationView | null
  /** The cached station views (relocation targets and their names), or none. */
  stations: readonly StationView[]
  /** Game time now (seconds). */
  now: number
  text?: AssetListText
  /** The shared state lines (default `berth.json`). */
  berthText?: BerthText
}

export interface BerthAssetItem {
  /** `good:<id>`, `ware:<id>` or `comp:<id>`. */
  key: string
  name: string
  amountLabel: string
}

export interface BerthAssetRow {
  stationId: StationId
  name: string
  access: BerthAccess['kind']
  /** "Open", "Frozen: station disabled", "Relocating to X on day N". */
  stateLabel: string
  /** The player is docked at this berth's station. */
  here: boolean
  /** No action from this list; true away from the station (and always, since the list has no buttons). */
  readOnly: true
  /** Where storing and taking happen ("Docked here: …" or "Read only: …"). */
  note: string
  tonnes: number
  components: number
  totalsLabel: string
  items: BerthAssetItem[]
}

export interface BerthAssetsModel {
  title: string
  sub: string
  closeLabel: string
  rows: BerthAssetRow[]
  berths: number
  tonnes: number
  components: number
  totalsLabel: string
  /** The empty line (no career, or nothing stored), else null. */
  empty: string | null
}

function componentName(data: M2Data, c: CargoComponent): string {
  const name = data.wares.components.find((d) => d.type === c.type)?.name ?? c.type
  const mk = c.mk === undefined ? undefined : MK_NAMES[c.mk - 1]
  return mk === undefined ? name : `${name} Mk ${mk}`
}

function stationName(input: BerthAssetsInput, id: StationId): string {
  const def = input.data.stations.stations.find((s) => s.id === id)
  if (def !== undefined) return def.name
  return input.stations.find((v) => isBerthStation(v, id))?.name ?? id
}

function stateLine(access: BerthAccess, view: StationView | null, input: BerthAssetsInput, career: Career, text: BerthText): string {
  if (access.kind === 'open') return text.open
  if (access.kind === 'frozen') return text.frozen
  const day = Math.floor(access.dueTime / DAY_S)
  const target = view === null ? null : relocationTarget(view, { stations: input.stations, hops: () => -1 }, career)
  if (target === null) return fill(text.relocating, { day })
  const name = input.stations.find((s) => s.id === target.view.id)?.name ?? target.view.id
  return fill(text.relocatingTo, { station: name, day })
}

function items(data: M2Data, career: Career, entry: BerthEntry): BerthAssetItem[] {
  const out: BerthAssetItem[] = []
  for (const g of GOOD_IDS) {
    const q = goodT(entry.goods, g)
    if (q > EPSILON) out.push({ key: `good:${g}`, name: GOOD_NAMES[g], amountLabel: `${formatTonnes(q)} t` })
  }
  for (const w of WARE_IDS) {
    const t = entry.wares[w] ?? 0
    if (t > EPSILON) out.push({ key: `ware:${w}`, name: wareName(data, career, w), amountLabel: `${formatTonnes(t)} t` })
  }
  for (const c of entry.components) out.push({ key: `comp:${c.id}`, name: componentName(data, c), amountLabel: `${formatTonnes(c.massT)} t` })
  return out
}

/** Tonnes in a berth: goods by mass, raw wares, and every component's mass. */
function berthTonnes(entry: BerthEntry): number {
  let t = goodsT(entry.goods)
  for (const w of WARE_IDS) t += entry.wares[w] ?? 0
  for (const c of entry.components) t += c.massT
  return t
}

/** The list while no career runs (no rows, the "No career" line). */
export function noCareerAssetsModel(text: AssetListText = ASSET_LIST_UI.text): BerthAssetsModel {
  return { title: text.title, sub: text.sub, closeLabel: text.close, rows: [], berths: 0, tonnes: 0, components: 0, totalsLabel: '', empty: text.noCareer }
}

/** The asset list's model. */
export function berthAssetsModel(input: BerthAssetsInput): BerthAssetsModel {
  const text = input.text ?? ASSET_LIST_UI.text
  const berthText = input.berthText ?? BERTH_UI.text
  const base = { title: text.title, sub: text.sub, closeLabel: text.close }
  const career = input.career
  if (career === null) return noCareerAssetsModel(text)

  const data: BerthData = input.data
  const rows: BerthAssetRow[] = []
  const storage = career.storage ?? {}
  for (const stationId of Object.keys(storage)) {
    const entry = storage[stationId]
    const view = input.viewOf(stationId)
    const access = berthAccess(entry, view, career, input.now, data)
    const here = input.dockedAt === stationId
    const tonnes = berthTonnes(entry)
    const components = entry.components.length
    rows.push({
      stationId,
      name: stationName(input, stationId),
      access: access.kind,
      stateLabel: stateLine(access, view, input, career, berthText),
      here,
      readOnly: true,
      note: here ? text.here : text.readOnly,
      tonnes,
      components,
      totalsLabel: fill(text.rowTotals, { tonnes: formatTonnes(tonnes), components: count(text.componentCount, components) }),
      items: items(input.data, career, entry),
    })
  }
  rows.sort((a, b) => (a.here !== b.here ? (a.here ? -1 : 1) : a.name !== b.name ? (a.name < b.name ? -1 : 1) : a.stationId < b.stationId ? -1 : a.stationId > b.stationId ? 1 : 0))

  let tonnes = 0
  let components = 0
  for (const r of rows) {
    tonnes += r.tonnes
    components += r.components
  }
  return {
    ...base,
    rows,
    berths: rows.length,
    tonnes,
    components,
    totalsLabel: fill(text.totals, { berths: count(text.berthCount, rows.length), tonnes: formatTonnes(tonnes), components: count(text.componentCount, components) }),
    empty: rows.length === 0 ? text.empty : null,
  }
}
